import {
  SPEED_OF_ME_API_KEY,
  SPEED_OF_ME_DEV_API_KEY,
} from '../features/TechCheckStage/vendor/speedofme/constants';
import {
  GOOGLE_MAPS_DEVELOPMENT_API_KEY,
  GOOGLE_MAPS_PRODUCTION_API_KEY,
  GOOGLE_MAPS_STAGING_API_KEY,
} from './google/constants';
import {
  TURNSTILE_PRODUCTION_SITE_KEY,
  TURNSTILE_STAGING_SITE_KEY,
  TURNSTILE_TEST_SITE_KEY,
} from './turnstile/constants';

interface DomainMeta {
  environment: 'dev' | 'stage' | 'uat' | 'prod';
  monolithOrigin: string;
  tenant: string;
  apiKey: {
    googleMaps: string;
    speedOfMe: string;
    turnstileSiteKey: string;
  };
}

// Searches for any base domain such as web.fountain.com. Will not match `app.web.fountain.com`.
const FOUNTAIN_DOMAIN_MATCHER = /^[^.]*\.fountain\.com/;
// Matches domains like "sandbox.amazon-na.fountain.com"
const FOUNTAIN_SANDBOX_SUBDOMAIN_MATCHER =
  /^sandbox\.[a-zA-Z0-9-]+\.fountain\.com/;
// Matches the staging domains such as stage-use-11.fountain.com
const FOUNTAIN_STAGING_DOMAIN_MATCHER = /^staging[^.]*\.fountain\.com/;
// Matches an RWX sandbox host in the fountain org. RWX serves one task under
// two hostname shapes: applicant-ui-alice--fountain.rwx.run (friendly) and
// 10228fac…--fountain.rwx.run (canonical, pinned to the task cache key).
// The region segment is optional so the legacy
// applicant-ui-alice--fountain.r1.rwx.run spelling still matches. `rwx.run` is a
// shared multi-tenant domain, so the `--fountain` org segment is required.
const FOUNTAIN_RWX_SANDBOX_MATCHER = /--fountain\.(?:[a-z0-9-]+\.)?rwx\.run$/;

const FOUNTAIN_DEV_DOMAINS = [
  'dev-01.fountain.com',
  'dev-02.fountain.com',
  'faut-02.fountain.com',
  'faut-03.fountain.com',
];

const FOUNTAIN_UAT_DOMAINS = ['uat-01.fountain.com', 'faut-01.fountain.com'];

const isStagingDomain = (hostname: string) => {
  if (FOUNTAIN_STAGING_DOMAIN_MATCHER.test(hostname)) {
    return true;
  }
  return false;
};

const isUatDomain = (hostname: string) => {
  if (FOUNTAIN_UAT_DOMAINS.includes(hostname)) {
    return true;
  }
  return false;
};

const isDevDomain = (hostname: string) => {
  if (FOUNTAIN_DEV_DOMAINS.includes(hostname)) {
    return true;
  }
  return false;
};

const isSandboxDomain = (hostname: string): boolean =>
  FOUNTAIN_SANDBOX_SUBDOMAIN_MATCHER.test(hostname);

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'localhost') {
    return {
      environment: 'dev',
      monolithOrigin: `http://localhost:${
        process.env.NODE_ENV === 'test' ? 3023 : 3000
      }`,
      tenant: 'local',
      apiKey: {
        googleMaps: GOOGLE_MAPS_DEVELOPMENT_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  if (hostname.endsWith('.chromatic.com')) {
    return {
      environment: 'dev',
      monolithOrigin: 'http://localhost:3000',
      tenant: 'storybook',
      apiKey: {
        googleMaps: GOOGLE_MAPS_DEVELOPMENT_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  if (isDevDomain(hostname)) {
    return {
      environment: 'dev',
      monolithOrigin: `https://${hostname}`,
      tenant: hostname.split('.')[0],
      apiKey: {
        googleMaps: GOOGLE_MAPS_STAGING_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  if (isStagingDomain(hostname)) {
    return {
      environment: 'stage',
      monolithOrigin: `https://${hostname}`,
      tenant: hostname.split('.')[0],
      apiKey: {
        googleMaps: GOOGLE_MAPS_STAGING_API_KEY,
        speedOfMe: SPEED_OF_ME_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  if (isUatDomain(hostname)) {
    return {
      environment: 'uat',
      monolithOrigin: `https://${hostname}`,
      tenant: hostname.split('.')[0],
      apiKey: {
        googleMaps: GOOGLE_MAPS_STAGING_API_KEY,
        speedOfMe: SPEED_OF_ME_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  if (isSandboxDomain(hostname)) {
    const subdomain = hostname.split('.')[1];

    return {
      environment: 'prod',
      monolithOrigin: `https://${hostname}`,
      tenant: subdomain,
      apiKey: {
        googleMaps: GOOGLE_MAPS_PRODUCTION_API_KEY,
        speedOfMe: SPEED_OF_ME_API_KEY,
        turnstileSiteKey: TURNSTILE_PRODUCTION_SITE_KEY,
      },
    };
  }

  if (FOUNTAIN_DOMAIN_MATCHER.test(hostname)) {
    const subdomain = hostname.split('.')[0];

    return {
      environment: 'prod',
      monolithOrigin: `https://${hostname}`,
      tenant: subdomain,
      apiKey: {
        googleMaps: GOOGLE_MAPS_PRODUCTION_API_KEY,
        speedOfMe: SPEED_OF_ME_API_KEY,
        turnstileSiteKey: TURNSTILE_PRODUCTION_SITE_KEY,
      },
    };
  }

  if (
    FOUNTAIN_RWX_SANDBOX_MATCHER.test(hostname) &&
    hostname.startsWith('applicant-ui-')
  ) {
    // RWX dev sandbox, friendly hostname. Strip the 'applicant-ui-' prefix and
    // replace with 'hire-' to derive the monolith URL.
    // e.g. applicant-ui-alice--fountain.rwx.run → hire-alice--fountain.rwx.run
    //
    // The canonical hostname carries an opaque hash instead of the prefix, so
    // no swap can derive a monolith host from it. It falls through to the
    // REACT_APP_MONOLITH_BASE_URL branch at the end of this function.
    const monolithHostname = hostname.replace(/^applicant-ui-/, 'hire-');
    return {
      environment: 'dev',
      monolithOrigin: `https://${monolithHostname}`,
      tenant: 'rwx-sandbox',
      apiKey: {
        googleMaps: GOOGLE_MAPS_DEVELOPMENT_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_TEST_SITE_KEY,
      },
    };
  }

  if (hostname.endsWith('.modal.host')) {
    // Open-Inspect / Modal sandbox tunnel (e.g. ta-<id>-<port>-<rand>.w.modal.host). Served behind
    // an nginx reverse proxy that routes API/auth paths to the monolith on the SAME origin,
    // so use a same-origin (empty) monolith origin. Mirrors the RWX sandbox case above.
    return {
      environment: 'dev',
      monolithOrigin: '',
      tenant: 'modal-sandbox',
      apiKey: {
        googleMaps: GOOGLE_MAPS_DEVELOPMENT_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_STAGING_SITE_KEY,
      },
    };
  }

  // Last resort for a host this function cannot map by shape. Only a sandbox
  // build bakes REACT_APP_MONOLITH_BASE_URL in (.rwx/hire-webapps.yml), so a
  // production build still throws below. This covers RWX's canonical,
  // task-cache-key-pinned hostname — visual QA drives those to pin the exact
  // commit a run built — whose opaque hash carries no slug to swap, and it
  // keeps the SPA rendering if RWX changes its hostname shape again.
  if (process.env.REACT_APP_MONOLITH_BASE_URL) {
    return {
      environment: 'dev',
      monolithOrigin: process.env.REACT_APP_MONOLITH_BASE_URL,
      tenant: 'rwx-sandbox',
      apiKey: {
        googleMaps: GOOGLE_MAPS_DEVELOPMENT_API_KEY,
        speedOfMe: SPEED_OF_ME_DEV_API_KEY,
        turnstileSiteKey: TURNSTILE_TEST_SITE_KEY,
      },
    };
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
